import { CardElement, Elements, useElements, useStripe } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { useMutation, useQuery } from '@tanstack/react-query';
import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';

import { useAuth } from '@dashboard/context/AuthContext/AuthContext';
import { useToast } from '@dashboard/context/ToastContext';

import useSellerQueries from '@dashboard/hooks/queries/useSellerQueries';

import { stripePublicKey } from '@dashboard/services/env';
import { formatPrice } from '@dashboard/services/formatting';
import logException from '@dashboard/services/logException';
import { fetcher } from '@dashboard/services/request';

import Button from '@dashboard/components/Button';
import { FormElement, Label } from '@dashboard/components/Form';
import { FormErrorBase } from '@dashboard/components/Formik/components/FormError';
import Spinner from '@dashboard/components/Spinner';

const PaymentDueForm = () => {
  // get the user's subscription
  // get the outstanding invoice
  // get the payment intent of the invoice
  // collect the payment information
  // confirm the payment
  // if successful, emit toast and redirect to the dashboard
  // if unsuccessful, show the error and allow the user to try again

  const { seller, sellerId, setSeller, token } = useAuth();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const { getInvoice } = useSellerQueries();
  const stripe = useStripe();
  const elements = useElements();
  const { emitToast } = useToast();
  const history = useHistory();

  const {
    data: invoice,
    isLoading: invoiceLoading,
    isError: invoiceError,
  } = useQuery({
    queryKey: ['invoices', sellerId, seller.stripeOutstandingInvoiceId],
    queryFn: () => getInvoice(sellerId, seller.stripeOutstandingInvoiceId),
    enabled: !!seller?.stripeOutstandingInvoiceId && !!sellerId,
  });

  const editPaymentMethodMutation = useMutation({
    mutationFn: intent => {
      return fetcher(`/sellers/${sellerId}/payment-method`, token, {
        method: 'POST',
        body: JSON.stringify({ intent }),
      });
    },
  });

  useEffect(() => {
    if (seller.stripeSubscriptionStatus === 'active' && history) {
      history.push('/');
    }
  }, [seller.stripeSubscriptionStatus, history]);

  async function confirmPayment(paymentIntent) {
    setIsSubmitting(true);

    const { paymentIntent: intent, error } = await stripe.confirmCardPayment(
      paymentIntent.client_secret,
      {
        payment_method: {
          card: elements.getElement(CardElement),
        },
        setup_future_usage: 'off_session',
      },
    );

    if (error) {
      setError(error.message);
      setIsSubmitting(false);
      return;
    }

    // set payment method as default
    try {
      await editPaymentMethodMutation.mutateAsync(intent);
    } catch (error) {
      logException({
        message: error.message,
        intent,
        seller,
      });
    }

    emitToast({
      title: 'Payment successful',
      type: 'success',
    });

    // set seller's subscription status to active
    // so that seller won't be redirected to this page
    setSeller({
      ...seller,
      stripeSubscriptionStatus: 'active',
    });

    window.setTimeout(() => {
      history.push('/');
      setIsSubmitting(false);
    }, 1000);
  }

  if (invoiceLoading) {
    return (
      <div className="flex justify-center items-center">
        <Spinner />
      </div>
    );
  }

  if (invoiceError) {
    return (
      <div className="flex justify-center items-center">
        Something went wrong. Please refresh and try again.
      </div>
    );
  }

  return (
    <div>
      <div className="flex justify-center items-center gap-x-4 mb-4">
        <div className="text-center text-gray-600 text-lg">
          Amount due:{' '}
          <span className="text-black font-medium">
            {formatPrice({
              amount: invoice.amount_due,
              currency: invoice.currency,
              style: 'currency',
            })}
          </span>
        </div>

        <Button
          appearance="secondary"
          size="small"
          href={invoice.hosted_invoice_url}
          target="_blank"
          external
        >
          View invoice
        </Button>
      </div>

      <FormElement>
        <Label htmlFor="stripe-card-element">Card</Label>
        <CardElement
          id="stripe-card-element"
          options={{
            style: {
              base: {
                iconColor: '#c0b6ba',
                fontWeight: 400,
                fontFamily:
                  '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen-Sans, Ubuntu, Cantarell, "Helvetica Neue", sans-serif',
                fontSmoothing: 'antialiased',
                background: 'white',
                fontSize: '16px',
                lineHeight: '1.5',
                padding: '8px',
                color: '#382b2f', // text-gray-800
                '::placeholder': {
                  color: '#c0b6ba',
                },
                '::focus-visible': {
                  outline: 'none',
                },
              },
            },
          }}
          className="bg-white placeholder-gray-200 border-none shadow-input focus:shadow-input-focus appearance-none block w-full py-2 px-2 rounded"
        />
      </FormElement>

      {error && (
        <FormElement>
          <FormErrorBase>{error}</FormErrorBase>
        </FormElement>
      )}

      <Button
        appearance="primary"
        size="full"
        onClick={() => confirmPayment(invoice.payment_intent)}
        disabled={isSubmitting}
      >
        {isSubmitting ? (
          <Spinner className="ml-2 h-7 w-7 !text-current" />
        ) : (
          `Pay ${formatPrice({ amount: invoice.amount_due, currency: invoice.currency })}`
        )}
      </Button>
    </div>
  );
};

const PaymentDuePage = () => {
  return (
    <Elements stripe={loadStripe(stripePublicKey)} options={{}}>
      <div className="max-w-xl mx-auto px-4 py-8">
        <div className="text-center mb-4">
          <h1 className="text-3xl font-bold mb-2">Subscription payment due</h1>
          <p className="text-lg text-gray-600">
            Your latest invoice payment failed. Please update your payment information to continue
            using Checkout Page.
          </p>
        </div>

        <div className="max-w-xl m-auto">
          <PaymentDueForm />
        </div>
      </div>
    </Elements>
  );
};

export default PaymentDuePage;
